import {
  createContext, useCallback, useContext, useMemo, useState, type ReactNode,
} from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { api, authStore, type StoredAuth } from './api';

interface AuthContextValue {
  auth: StoredAuth | null;
  signedIn: boolean;
  login: (email: string, password: string, captchaToken?: string) => Promise<void>;
  register: (input: {
    email: string; password: string; displayName?: string; organisationName?: string;
    captchaToken?: string;
  }) => Promise<void>;
  logout: () => void;

  /**
   * Adopts a session obtained some other way than a password.
   *
   * Exists for the single-sign-on return trip, where the tokens are fetched by the page
   * that redeems the one-time code rather than by this context. It takes the same shape
   * a password sign-in produces, so there is still one thing stored and one way to store
   * it.
   */
  adopt: (session: StoredAuth) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [auth, setAuth] = useState<StoredAuth | null>(() => authStore.read());

  const login = useCallback(async (email: string, password: string, captchaToken?: string) => {
    const result = await api.auth.login({ email, password, captchaToken });
    authStore.write(result);
    setAuth(result);
  }, []);

  const register = useCallback(async (input: {
    email: string; password: string; displayName?: string; organisationName?: string;
    captchaToken?: string;
  }) => {
    const result = await api.auth.register(input);
    authStore.write(result);
    setAuth(result);
  }, []);

  const adopt = useCallback((session: StoredAuth) => {
    authStore.write(session);
    setAuth(session);
  }, []);

  const logout = useCallback(() => {
    authStore.clear();
    setAuth(null);
  }, []);

  const value = useMemo(
    () => ({ auth, signedIn: auth !== null, login, register, logout, adopt }),
    [auth, login, register, logout, adopt],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider.');
  return context;
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { signedIn } = useAuth();
  const location = useLocation();

  // Carry the attempted path so sign-in returns the user where they were going.
  return signedIn
    ? <>{children}</>
    : <Navigate to="/login" replace state={{ from: location.pathname }} />;
}
